import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ScanResult, PendingAction } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Package, MapPin, Clock, AlertCircle,
  CheckCircle, Truck, Settings, Wrench, ChevronRight,
  PlayCircle, User, X, Euro, QrCode, XCircle, Loader2,
  Search, Filter, ArrowRight, ChevronDown
} from 'lucide-react';
import QrScanner from '../components/QrScanner';

// ============================================================================
// CONFIGURATIONS
// ============================================================================

const actionTypeConfig: Record<string, { label: string; icon: any; color: string; bgColor: string }> = {
  moved: { label: 'Spostamento', icon: Truck, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  maintenance: { label: 'Manutenzione', icon: Settings, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  repair: { label: 'Riparazione', icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-100' }
};

const conditionConfig: Record<string, { label: string; emoji: string }> = {
  excellent: { label: 'Eccellente', emoji: '✨' },
  good: { label: 'Buono', emoji: '👍' },
  fair: { label: 'Discreto', emoji: '👌' },
  poor: { label: 'Scarso', emoji: '⚠️' },
  broken: { label: 'Rotto', emoji: '❌' }
};

// ============================================================================
// ASSET SELECT COMPONENT (dropdown with search, no multi-select)
// ============================================================================

interface AssetOption {
  id: string;
  name: string;
}

function AssetSelect({ 
  assets, 
  selected, 
  onChange 
}: { 
  assets: AssetOption[]; 
  selected: string; 
  onChange: (id: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredAssets = assets.filter(asset => 
    asset.name.toLowerCase().includes(search.toLowerCase())
  );

  const selectedAsset = assets.find(a => a.id === selected);

  const handleSelect = (id: string) => {
    onChange(id);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="input w-full text-left flex items-center justify-between gap-2 text-sm"
      >
        <div className="flex items-center gap-2 truncate">
          <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="truncate">
            {selectedAsset ? selectedAsset.name : <span className="text-gray-500">Asset</span>}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca asset..."
                className="input input-sm pl-7 w-full"
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredAssets.length === 0 ? (
              <div className="p-3 text-sm text-gray-500 text-center">Nessun asset trovato</div>
            ) : (
              filteredAssets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => handleSelect(asset.id)}
                  className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 ${
                    selected === asset.id ? 'bg-primary-50' : ''
                  }`}
                >
                  <span className="text-sm text-gray-700 truncate">{asset.name}</span>
                </button>
              ))
            )}
          </div>
          {selected && (
            <div className="p-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Cancella selezione
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MODAL COMPONENT
// ============================================================================

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative inline-block w-full max-w-lg p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RESULT MODAL COMPONENT
// ============================================================================

interface ResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'success' | 'error';
  title: string;
  message: string;
  details?: string;
}

function ResultModal({ isOpen, onClose, type, title, message, details }: ResultModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative inline-block w-full max-w-md p-6 my-8 text-center align-middle bg-white rounded-2xl shadow-xl transform transition-all animate-slideInUp">
          <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
            type === 'success' ? 'bg-emerald-100' : 'bg-red-100'
          }`}>
            {type === 'success' ? (
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            ) : (
              <XCircle className="w-8 h-8 text-red-600" />
            )}
          </div>
          <h3 className={`text-xl font-bold mb-2 ${
            type === 'success' ? 'text-emerald-800' : 'text-red-800'
          }`}>
            {title}
          </h3>
          <p className="text-gray-600 mb-2">{message}</p>
          {details && (
            <p className="text-sm text-gray-500 mb-4">{details}</p>
          )}
          <button
            onClick={onClose}
            className={`w-full btn ${
              type === 'success' 
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PendingActions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [filter, setFilter] = useState<'all' | 'own' | 'moved' | 'maintenance' | 'repair'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<PendingAction | null>(null);
  const [completeForm, setCompleteForm] = useState({ cost: 0, newCondition: 'none', description: '' });
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [scanningForMoveCompletion, setScanningForMoveCompletion] = useState<PendingAction | null>(null);
  
  // New states for multi-step scan flow
  const [scanStep, setScanStep] = useState<'asset' | 'location'>('location');
  const [assetVerified, setAssetVerified] = useState(false);
  
  // Success screen after asset scan (before location scan)
  const [assetScanSuccess, setAssetScanSuccess] = useState(false);
  
  // Result modal state
  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
    details?: string;
  }>({ isOpen: false, type: 'success', title: '', message: '' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['pending-actions'],
    queryFn: () => api.getPendingActions(),
    refetchInterval: 30000
  });

  const completeMutation = useMutation({
    mutationFn: (params: { assetId: string; historyId: string; cost: number; newCondition: string; description?: string }) =>
      api.updateAsset(params.assetId, {
        action: 'completeIntervention',
        historyId: params.historyId,
        cost: params.cost,
        newCondition: params.newCondition,
        description: params.description
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      
      // If completing a move via QR scan, show success modal
      if (scanningForMoveCompletion) {
        const action = scanningForMoveCompletion;
        setResultModal({
          isOpen: true,
          type: 'success',
          title: 'Spostamento Completato!',
          message: `L'asset "${action.asset.name}" è stato spostato con successo.`,
          details: action.toLocation ? `Nuova ubicazione: ${action.toLocation.name}` : undefined
        });
        setScanningForMoveCompletion(null);
        setScanStep('location');
        setAssetVerified(false);
      }
      
      setCompleteModalOpen(false);
      setSelectedAction(null);
      setCompleteForm({ cost: 0, newCondition: 'none', description: '' });
    },
    onError: (error: Error) => {
      if (scanningForMoveCompletion) {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Errore',
          message: 'Impossibile completare lo spostamento.',
          details: error.message || 'Si è verificato un errore durante il completamento.'
        });
      }
    }
  });

  const handleQrScanResult = async (result: ScanResult) => {
    if (!scanningForMoveCompletion) return;
    
    const action = scanningForMoveCompletion;
    const isOwnAction = action.isOwnAction;
    
    // If not own action, need to scan asset first
    if (!isOwnAction && scanStep === 'asset') {
      if (result.found && result.type === 'asset') {
        // Check if scanned asset matches the expected asset
        if (result.qrCode?.toUpperCase() === action.asset.qrCode.toUpperCase() ||
            result.id === action.asset.id) {
          // Asset verified - show success screen before proceeding to location
          setAssetVerified(true);
          setQrScannerOpen(false);
          setAssetScanSuccess(true);
        } else {
          setResultModal({
            isOpen: true,
            type: 'error',
            title: 'Asset Non Corretto',
            message: `Hai scansionato un asset diverso da quello atteso.`,
            details: `Asset atteso: ${action.asset.name} (${action.asset.qrCode})`
          });
          setQrScannerOpen(false);
        }
      } else if (result.found && result.type === 'location') {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Scansione Non Valida',
          message: 'Devi prima scansionare il QR code dell\'asset per verificare di averlo in possesso.',
          details: `Asset da scansionare: ${action.asset.name} (${action.asset.qrCode})`
        });
        setQrScannerOpen(false);
      }
      return;
    }
    
    // Scanning for location (either own action or asset already verified)
    if (result.found && result.type === 'location') {
      // Check if scanned location matches the expected destination
      if (action.toLocation && (
        result.qrCode?.toUpperCase() === action.toLocation.qrCode?.toUpperCase() ||
        result.id === action.toLocation.id
      )) {
        // Complete the move
        setQrScannerOpen(false);
        completeMutation.mutate({
          assetId: action.asset.id,
          historyId: action.id,
          cost: 0,
          newCondition: 'none'
        });
      } else {
        // Wrong location
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Ubicazione Non Corretta',
          message: 'L\'ubicazione scansionata non corrisponde alla destinazione prevista.',
          details: `Destinazione attesa: ${action.toLocation?.name}`
        });
        setQrScannerOpen(false);
      }
    } else if (result.found && result.type === 'asset') {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Scansione Non Valida',
        message: 'Hai scansionato un asset invece di un\'ubicazione.',
        details: `Scansiona il QR code dell'ubicazione "${action.toLocation?.name}" per completare lo spostamento.`
      });
      setQrScannerOpen(false);
    }
  };

  // Continue to location scan after asset verification
  const continueToLocationScan = () => {
    setAssetScanSuccess(false);
    setScanStep('location');
    setTimeout(() => setQrScannerOpen(true), 100);
  };

  const openCompleteModal = (action: PendingAction) => {
    // For moves, try QR completion first or complete immediately
    if (action.actionType === 'moved') {
      // Ask user if they want to scan or complete manually
      setSelectedAction(action);
      setCompleteForm({ cost: 0, newCondition: 'none', description: action.description || '' });
      setCompleteModalOpen(true);
    } else {
      // For maintenance/repair, show the full modal
      setSelectedAction(action);
      setCompleteForm({ cost: 0, newCondition: 'none', description: action.description || '' });
      setCompleteModalOpen(true);
    }
  };

  const handleComplete = () => {
    if (!selectedAction) return;
    
    completeMutation.mutate({
      assetId: selectedAction.asset.id,
      historyId: selectedAction.id,
      cost: completeForm.cost,
      newCondition: completeForm.newCondition,
      description: completeForm.description
    });
  };

  const startMoveQrScan = (action: PendingAction) => {
    setScanningForMoveCompletion(action);
    
    // If not the owner, need to scan asset first
    if (!action.isOwnAction) {
      setScanStep('asset');
      setAssetVerified(false);
    } else {
      setScanStep('location');
      setAssetVerified(true);
    }
    
    setQrScannerOpen(true);
    setCompleteModalOpen(false);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ora';
    if (minutes < 60) return `${minutes}m fa`;
    if (hours < 24) return `${hours}h fa`;
    if (days < 7) return `${days}g fa`;
    return formatDate(timestamp);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento azioni in sospeso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Errore caricamento'}</span>
        </div>
      </div>
    );
  }

  const { pendingActions, stats } = data!;

  // Get unique users for filter
  const uniqueUsers = Array.from(new Set(
    pendingActions
      .filter(a => a.startedBy)
      .map(a => `${a.startedBy!.firstName} ${a.startedBy!.lastName}`)
  )).sort();

  // Get unique assets for filter
  const uniqueAssets = Array.from(
    new Map(pendingActions.map(a => [a.asset.id, { id: a.asset.id, name: a.asset.name }])).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

  // Filter actions with all filters
  const filteredActions = pendingActions.filter((action: PendingAction) => {
    // Type filter
    if (filter === 'own' && !action.isOwnAction) return false;
    if (filter !== 'all' && filter !== 'own' && action.actionType !== filter) return false;
    
    // Asset filter
    if (assetFilter && action.asset.id !== assetFilter) return false;
    
    // User filter
    if (userFilter && action.startedBy) {
      const fullName = `${action.startedBy.firstName} ${action.startedBy.lastName}`;
      if (fullName !== userFilter) return false;
    }
    
    // Text search (asset name, QR code, description, location)
    if (searchQuery.trim()) {
      const search = searchQuery.toLowerCase();
      const matchesAsset = action.asset.name.toLowerCase().includes(search) ||
                          action.asset.qrCode.toLowerCase().includes(search);
      const matchesDescription = action.description?.toLowerCase().includes(search);
      const matchesLocation = action.toLocation?.name.toLowerCase().includes(search) ||
                              action.fromLocation?.name.toLowerCase().includes(search);
      if (!matchesAsset && !matchesDescription && !matchesLocation) return false;
    }
    
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Azioni in Sospeso</h1>
          <p className="page-subtitle">
            {stats.total} {stats.total === 1 ? 'azione' : 'azioni'} da completare
          </p>
        </div>
      </div>

      {/* Quick Filters - Stats Cards - Mobile: 2 rows (2+3), Desktop: 1 row */}
      <div className="space-y-2 sm:space-y-0">
        {/* First row: Totale + Le mie */}
        <div className="grid grid-cols-2 sm:hidden gap-2">
          <button
            onClick={() => {
              setFilter('all');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'all' ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50' : ''}`}
          >
            <div className="flex items-center gap-1.5">
              <div className="p-1 rounded-lg bg-gray-100 flex-shrink-0">
                <Package className="w-3.5 h-3.5 text-gray-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900">{stats.total}</p>
                <p className="text-[10px] text-gray-500 font-medium truncate">Totale</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('own');
              if (user) {
                setUserFilter(`${user.firstName} ${user.lastName}`);
              }
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'own' ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50' : ''}`}
          >
            <div className="flex items-center gap-1.5">
              <div className="p-1 rounded-lg bg-purple-100 flex-shrink-0">
                <User className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900">{stats.own}</p>
                <p className="text-[10px] text-gray-500 font-medium truncate">Le mie</p>
              </div>
            </div>
          </button>
        </div>

        {/* Second row on mobile: Spostamenti + Manutenzioni + Riparazioni */}
        <div className="grid grid-cols-3 sm:hidden gap-2">
          <button
            onClick={() => {
              setFilter('moved');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'moved' ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50' : ''}`}
          >
            <div className="flex items-center gap-1">
              <div className="p-1 rounded-lg bg-indigo-100 flex-shrink-0">
                <Truck className="w-3.5 h-3.5 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900">{stats.moves}</p>
                <p className="text-[10px] text-gray-500 font-medium truncate">Spostam.</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('maintenance');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'maintenance' ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50' : ''}`}
          >
            <div className="flex items-center gap-1">
              <div className="p-1 rounded-lg bg-blue-100 flex-shrink-0">
                <Settings className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900">{stats.maintenance}</p>
                <p className="text-[10px] text-gray-500 font-medium truncate">Manut.</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('repair');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'repair' ? 'ring-2 ring-orange-500 border-orange-500 bg-orange-50' : ''}`}
          >
            <div className="flex items-center gap-1">
              <div className="p-1 rounded-lg bg-orange-100 flex-shrink-0">
                <Wrench className="w-3.5 h-3.5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900">{stats.repairs}</p>
                <p className="text-[10px] text-gray-500 font-medium truncate">Riparaz.</p>
              </div>
            </div>
          </button>
        </div>

        {/* Desktop: All 5 filters in one row */}
        <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <button
            onClick={() => {
              setFilter('all');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'all' ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 flex-shrink-0">
                <Package className="w-4 h-4 text-gray-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-gray-500 font-medium truncate">Totale</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('own');
              if (user) {
                setUserFilter(`${user.firstName} ${user.lastName}`);
              }
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'own' ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 flex-shrink-0">
                <User className="w-4 h-4 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900">{stats.own}</p>
                <p className="text-xs text-gray-500 font-medium truncate">Le mie</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('moved');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'moved' ? 'ring-2 ring-indigo-500 border-indigo-500 bg-indigo-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100 flex-shrink-0">
                <Truck className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900">{stats.moves}</p>
                <p className="text-xs text-gray-500 font-medium truncate">Spostamenti</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('maintenance');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'maintenance' ? 'ring-2 ring-blue-500 border-blue-500 bg-blue-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 flex-shrink-0">
                <Settings className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900">{stats.maintenance}</p>
                <p className="text-xs text-gray-500 font-medium truncate">Manutenzioni</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => {
              setFilter('repair');
              setUserFilter('');
            }}
            className={`card card-sm text-left hover:border-gray-300 transition-colors ${filter === 'repair' ? 'ring-2 ring-orange-500 border-orange-500 bg-orange-50' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 flex-shrink-0">
                <Wrench className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-2xl font-bold text-gray-900">{stats.repairs}</p>
                <p className="text-xs text-gray-500 font-medium truncate">Riparazioni</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Enterprise Filters */}
      <div className="card">
        <div className="flex flex-col gap-3">
          {/* Search - Full width */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca per asset, QR code, descrizione..."
              className="input pl-10 w-full"
            />
          </div>
          
          {/* Asset + User filters on same row (also on mobile) */}
          <div className="flex flex-row gap-2 sm:gap-3">
            {/* Asset Filter with dropdown and search */}
            <div className="flex-1 min-w-0">
              <AssetSelect
                assets={uniqueAssets}
                selected={assetFilter}
                onChange={setAssetFilter}
              />
            </div>
            
            {/* User Filter */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    // Sync quick filter when user filter changes
                    if (e.target.value === '' || (user && e.target.value !== `${user.firstName} ${user.lastName}`)) {
                      if (filter === 'own') setFilter('all');
                    }
                  }}
                  className="input pl-10 w-full text-sm"
                >
                  <option value="">Utente</option>
                  {uniqueUsers.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Clear filters */}
            {(searchQuery || userFilter || assetFilter || filter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setUserFilter('');
                  setAssetFilter('');
                  setFilter('all');
                }}
                className="btn btn-secondary text-sm whitespace-nowrap flex-shrink-0 px-2 sm:px-3"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Cancella</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Actions List */}
      {filteredActions.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {filter === 'all' && !searchQuery && !userFilter && !assetFilter
              ? 'Nessuna azione in sospeso' 
              : 'Nessun risultato'}
          </h3>
          <p className="text-gray-500 text-sm">
            {filter === 'all' && !searchQuery && !userFilter && !assetFilter
              ? 'Tutte le azioni sono state completate!'
              : 'Prova a modificare i filtri di ricerca.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredActions.map((action: PendingAction) => {
            const config = actionTypeConfig[action.actionType] || actionTypeConfig.maintenance;
            const ActionIcon = config.icon;
            const condition = conditionConfig[action.asset.condition];

            return (
              <div
                key={action.id}
                className="card hover:border-gray-300 transition-colors"
              >
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center gap-4">
                  {/* Action Type Icon */}
                  <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <ActionIcon className={`w-6 h-6 ${config.color}`} />
                  </div>

                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    {/* Row 1: Action type + destination */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`font-semibold ${config.color}`}>{config.label}</span>
                      {action.actionType === 'moved' && action.toLocation && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="flex items-center gap-1 text-gray-700">
                            <MapPin className="w-3.5 h-3.5" />
                            <ArrowRight className="w-3 h-3" />
                            <span className="font-medium">{action.toLocation.name}</span>
                          </span>
                        </>
                      )}
                    </div>
                    
                    {/* Row 2: Asset info + who/when */}
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <Link
                        to={`/assets/${action.asset.id}`}
                        className="font-medium text-gray-900 hover:text-primary-600 transition-colors"
                      >
                        {action.asset.name}
                      </Link>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-mono text-xs">
                        {action.asset.qrCode}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3.5 h-3.5" />
                        {formatTimeAgo(action.createdAt)}
                      </span>
                      {action.startedBy && (
                        <span className="flex items-center gap-1.5 text-xs text-gray-500">
                          <User className="w-3.5 h-3.5" />
                          {action.startedBy.firstName} {action.startedBy.lastName}
                          {action.isOwnAction && (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded font-medium">Tu</span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions - aligned with content */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => openCompleteModal(action)}
                      disabled={completeMutation.isPending}
                      className="btn btn-primary text-sm flex items-center gap-1.5"
                    >
                      {completeMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <PlayCircle className="w-4 h-4" />
                      )}
                      Completa
                    </button>
                    <Link
                      to={`/assets/${action.asset.id}`}
                      className="icon-btn"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  </div>
                </div>

                {/* Mobile Layout */}
                <div className="sm:hidden relative">
                  {/* "Tu" badge - person icon in top right */}
                  {action.isOwnAction && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center z-10">
                      <User className="w-3.5 h-3.5 text-purple-600" />
                    </div>
                  )}
                  
                  <div className="flex items-start gap-3">
                    {/* Action Type Icon */}
                    <div className={`w-10 h-10 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <ActionIcon className={`w-5 h-5 ${config.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pr-4">
                      {/* Row 1: Action type */}
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`font-semibold text-sm ${config.color}`}>{config.label}</span>
                      </div>
                      
                      {/* Row 2: Asset info */}
                      <div className="flex items-center gap-2 mb-1.5">
                        <Link
                          to={`/assets/${action.asset.id}`}
                          className="font-medium text-sm text-gray-900 hover:text-primary-600 transition-colors truncate max-w-[150px]"
                        >
                          {action.asset.name}
                        </Link>
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono text-[10px]">
                          {action.asset.qrCode}
                        </span>
                      </div>

                      {/* Row 3: Who/when */}
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatTimeAgo(action.createdAt)}
                        </span>
                        {action.startedBy && (
                          <span className="flex items-center gap-1 truncate">
                            <span className="truncate">{action.startedBy.firstName} {action.startedBy.lastName}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Destination row with Completa button for moves */}
                  {action.actionType === 'moved' && action.toLocation ? (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <div className="flex items-center gap-1.5 text-gray-600 text-xs flex-1 min-w-0">
                        <MapPin className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                        <ArrowRight className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                        <span className="font-medium text-indigo-700 truncate">{action.toLocation.name}</span>
                      </div>
                      <button
                        onClick={() => openCompleteModal(action)}
                        disabled={completeMutation.isPending}
                        className="btn btn-primary text-sm flex items-center justify-center gap-1.5 px-3 flex-shrink-0"
                      >
                        {completeMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <PlayCircle className="w-4 h-4" />
                        )}
                        Completa
                      </button>
                      <Link
                        to={`/assets/${action.asset.id}`}
                        className="icon-btn flex-shrink-0"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  ) : (
                    /* Actions row for non-move actions */
                    <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t">
                      <button
                        onClick={() => openCompleteModal(action)}
                        disabled={completeMutation.isPending}
                        className="btn btn-primary text-sm flex items-center justify-center gap-1.5 px-3"
                      >
                        {completeMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <PlayCircle className="w-4 h-4" />
                        )}
                        Completa
                      </button>
                      <Link
                        to={`/assets/${action.asset.id}`}
                        className="icon-btn flex-shrink-0"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Complete Modal */}
      <Modal 
        isOpen={completeModalOpen} 
        onClose={() => {
          setCompleteModalOpen(false);
          setSelectedAction(null);
        }} 
        title={selectedAction?.actionType === 'moved' ? 'Completa Spostamento' : 'Completa Intervento'}
      >
        {selectedAction && (
          <div className="space-y-4">
            {/* Action Info */}
            <div className="p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${actionTypeConfig[selectedAction.actionType]?.bgColor || 'bg-gray-100'} flex items-center justify-center`}>
                  {(() => {
                    const Icon = actionTypeConfig[selectedAction.actionType]?.icon || Package;
                    return <Icon className={`w-5 h-5 ${actionTypeConfig[selectedAction.actionType]?.color || 'text-gray-600'}`} />;
                  })()}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedAction.asset.name}</p>
                  <p className="text-xs text-gray-500">
                    {actionTypeConfig[selectedAction.actionType]?.label || selectedAction.actionType}
                    {selectedAction.actionType === 'moved' && selectedAction.toLocation && (
                      <> → {selectedAction.toLocation.name}</>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Move-specific: QR Scan option with clear instructions */}
            {selectedAction.actionType === 'moved' && selectedAction.toLocation && (
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <QrCode className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-indigo-900 mb-1">Completa con QR Code</h4>
                    <p className="text-sm text-indigo-700">
                      {selectedAction.isOwnAction 
                        ? 'Scansiona il QR code dell\'ubicazione di destinazione per confermare lo spostamento.'
                        : 'Dato che non sei tu ad aver avviato questo spostamento, dovrai prima scansionare il QR dell\'asset per confermare di averlo in possesso.'}
                    </p>
                  </div>
                </div>
                
                {/* Step indicators for non-own actions */}
                {!selectedAction.isOwnAction && (
                  <div className="flex items-center gap-2 mb-4 text-xs">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg">
                      <span className="w-5 h-5 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                      <span>Scansiona Asset</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-indigo-400" />
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-500 rounded-lg">
                      <span className="w-5 h-5 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold">2</span>
                      <span>Scansiona Destinazione</span>
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => startMoveQrScan(selectedAction)}
                  className="w-full btn flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <QrCode className="w-4 h-4" />
                  {selectedAction.isOwnAction ? 'Scansiona QR Destinazione' : 'Inizia Scansione'}
                </button>
              </div>
            )}

            {/* Maintenance/Repair fields */}
            {selectedAction.actionType !== 'moved' && (
              <>
                <div className="form-group">
                  <label className="form-label">Descrizione intervento</label>
                  <textarea
                    value={completeForm.description}
                    onChange={(e) => setCompleteForm({ ...completeForm, description: e.target.value })}
                    className="input"
                    rows={3}
                    placeholder="Descrizione dell'intervento..."
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Condizione finale</label>
                  <select
                    value={completeForm.newCondition}
                    onChange={(e) => setCompleteForm({ ...completeForm, newCondition: e.target.value })}
                    className="input"
                  >
                    <option value="none">Nessun cambio</option>
                    {Object.entries(conditionConfig).map(([value, config]) => (
                      <option key={value} value={value}>{config.emoji} {config.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label flex items-center gap-1">
                    <Euro className="w-3.5 h-3.5" />
                    Costo intervento (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={completeForm.cost || ''}
                    onChange={(e) => setCompleteForm({ ...completeForm, cost: parseFloat(e.target.value) || 0 })}
                    className="input"
                    placeholder="0.00"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setCompleteModalOpen(false);
                  setSelectedAction(null);
                }}
                className="flex-1 btn btn-secondary"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleComplete}
                disabled={completeMutation.isPending}
                className="flex-1 btn btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {completeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                {completeMutation.isPending ? 'Completamento...' : 'Completa'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* QR Scanner for Move Completion */}
      {qrScannerOpen && scanningForMoveCompletion && (
        <QrScanner
          isOpen={qrScannerOpen}
          onClose={() => {
            setQrScannerOpen(false);
            setScanningForMoveCompletion(null);
            setScanStep('location');
            setAssetVerified(false);
          }}
          onScanResult={handleQrScanResult}
          navigateOnScan={false}
          title={
            scanStep === 'asset' 
              ? 'STEP 1: Scansiona Asset'
              : 'STEP 2: Scansiona Destinazione'
          }
          hint={
            scanStep === 'asset'
              ? `⚠️ Prima di tutto devi scansionare il QR code dell'asset "${scanningForMoveCompletion.asset.name}" per confermare che lo hai in possesso.`
              : `📍 Ora scansiona il QR code dell'ubicazione di destinazione "${scanningForMoveCompletion.toLocation?.name}" per completare lo spostamento.`
          }
        />
      )}

      {/* Asset Scan Success Modal - Step between asset and location scan */}
      {assetScanSuccess && scanningForMoveCompletion && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-center align-middle bg-white rounded-2xl shadow-xl transform transition-all animate-slideInUp">
              <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold text-emerald-800 mb-2">
                Asset Verificato!
              </h3>
              <p className="text-gray-600 mb-2">
                Hai confermato di avere in possesso l'asset:
              </p>
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="font-semibold text-gray-900">{scanningForMoveCompletion.asset.name}</p>
                <p className="text-sm text-gray-500 font-mono">{scanningForMoveCompletion.asset.qrCode}</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-indigo-800">
                  <strong>Prossimo step:</strong> Scansiona il QR code dell'ubicazione di destinazione <strong>"{scanningForMoveCompletion.toLocation?.name}"</strong> per completare lo spostamento.
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAssetScanSuccess(false);
                    setScanningForMoveCompletion(null);
                    setScanStep('location');
                    setAssetVerified(false);
                  }}
                  className="flex-1 btn btn-secondary"
                >
                  Annulla
                </button>
                <button
                  onClick={continueToLocationScan}
                  className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                >
                  <QrCode className="w-4 h-4" />
                  Scansiona Destinazione
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Result Modal */}
      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={() => setResultModal({ ...resultModal, isOpen: false })}
        type={resultModal.type}
        title={resultModal.title}
        message={resultModal.message}
        details={resultModal.details}
      />
    </div>
  );
}
